import React, { useState } from 'react';
import { Phone, Mail, Send, CheckCircle2, Star, ShieldCheck, Zap, Users, ArrowRight, Clock, MessageCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "mainEntity": {
    "@type": "EducationalOrganization",
    "name": "PIT (Project Info Tech)",
    "description": "Premium IT training and career transformation hub providing top-notch mentorship, placement support, and advanced technical skills.",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-7782918158",
      "email": "projectinfotech.in@gmail.com",
      "contactType": "customer support"
    }
  }
};

const ContactPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    course: "Java Full Stack Development", // Default pathway
    category: "Career Counseling" // Default category for backend
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    try {
      const response = await fetch('https://www.projectinfotech.in/api/registration/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            mobile: formData.mobile,
            category: formData.category,
            course: formData.course
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: "", mobile: "", email: "", course: "Java Full Stack Development", category: "Career Counseling" });
      } else {
        setErrorMsg("Something went wrong. Please try again later.");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setErrorMsg("Failed to connect to the server. Please check your network.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fcfcfc] text-[#1a1a1a] font-sans selection:bg-red-100 selection:text-red-900">
      <Helmet>
        <title>Contact Us | Transform Your IT Career | PIT</title>
        <meta name="description" content="Get in touch with PIT (Project InfoTech) for premium IT training, career counseling, and placement support. Start your journey today." />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
      </Helmet>

      {/* --- Section 1: Premium Header --- */}
      <div className="pt-32 pb-16 px-6 bg-white border-b border-gray-100">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <div className="max-w-3xl">
            <span className="text-red-600 font-bold tracking-widest uppercase text-sm mb-4 block flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></div>
              Connect with Excellence
            </span>
            <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-tight">
              Start your <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800">IT Revolution.</span>
            </h1>
          </div>
          <div className="text-gray-500 font-medium max-w-sm border-l-4 border-red-600 pl-6 py-2 text-lg">
            The most trusted premium career transformation hub. Expert mentorship to elevate your technical journey.
          </div>
        </motion.div>
      </div>

      {/* --- Section 2: Interactive Grid & Form --- */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          
          {/* Left: Brand Trust & Motivation */}
          <div className="lg:col-span-5 space-y-16">
            <div>
              <h2 className="text-3xl font-bold mb-10 border-b pb-4 inline-block">Why PIT stands out?</h2>
              <div className="space-y-10">
                <motion.div whileHover={{ x: 10 }} className="flex gap-6 group transition-all">
                  <div className="w-16 h-16 shrink-0 bg-white shadow-lg border border-gray-50 rounded-2xl flex items-center justify-center text-red-600 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                    <Star size={30} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold mb-2">Premium Mentorship</h4>
                    <p className="text-gray-500 leading-relaxed">Learn directly from architects who build highly scalable systems for top-tier companies.</p>
                  </div>
                </motion.div>

                <motion.div whileHover={{ x: 10 }} className="flex gap-6 group transition-all">
                  <div className="w-16 h-16 shrink-0 bg-white shadow-lg border border-gray-50 rounded-2xl flex items-center justify-center text-red-600 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                    <ShieldCheck size={30} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold mb-2">Placement Shield</h4>
                    <p className="text-gray-500 leading-relaxed">Our dedicated cell works relentlessly until you get placed. No student is left behind.</p>
                  </div>
                </motion.div>

                <motion.div whileHover={{ x: 10 }} className="flex gap-6 group transition-all">
                  <div className="w-16 h-16 shrink-0 bg-white shadow-lg border border-gray-50 rounded-2xl flex items-center justify-center text-red-600 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                    <Zap size={30} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold mb-2">Accelerated Learning</h4>
                    <p className="text-gray-500 leading-relaxed">Cut the fluff. Focus purely on modern tech stacks and skills that actually get you hired.</p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Support Links */}
            <div className="p-10 bg-gradient-to-br from-gray-50 to-gray-100 rounded-[2.5rem] space-y-6 shadow-inner border border-gray-200">
               <h4 className="font-bold text-gray-400 uppercase text-sm tracking-widest flex items-center gap-2">
                 <Phone size={16} /> Official Channels
               </h4>
               <div>
                 <a href="tel:+917782918158" className="block text-3xl font-black text-gray-900 hover:text-red-600 transition-colors mb-2">+91 7782918158</a>
                 <a href="mailto:projectinfotech.in@gmail.com" className="block text-lg font-medium text-gray-600 hover:text-red-600 transition-colors break-all">projectinfotech.in@gmail.com</a>
               </div>
            </div>
          </div>

          {/* Right: The Dynamic Form (Connected to Backend) */}
          <div className="lg:col-span-7">
            <div className="sticky top-24">
              <div className="bg-white rounded-[3rem] p-10 md:p-14 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)] border border-gray-100 relative overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute -top-32 -right-32 w-96 h-96 bg-gradient-to-br from-red-50 to-transparent rounded-full opacity-60 blur-3xl pointer-events-none"></div>

                {submitted ? (
                  <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }} 
                    animate={{ scale: 1, opacity: 1 }} 
                    className="text-center py-20"
                  >
                    <div className="w-24 h-24 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-sm">
                      <CheckCircle2 size={48} />
                    </div>
                    <h2 className="text-4xl font-black mb-4 text-gray-900">Application Received!</h2>
                    <p className="text-gray-500 mb-10 text-lg">Thank you for choosing PIT. Our career counselor will review your profile and contact you shortly.</p>
                    <button 
                      onClick={() => setSubmitted(false)} 
                      className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-full font-bold hover:bg-red-600 transition-colors"
                    >
                      Submit Another Query <ArrowRight size={18} />
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="relative z-10 space-y-10">
                    <div>
                      <h3 className="text-4xl font-black mb-3">Book a Free Session</h3>
                      <p className="text-gray-500 text-lg">Limited slots available for this week's expert counseling.</p>
                    </div>

                    {errorMsg && (
                      <div className="p-4 bg-red-50 text-red-600 rounded-xl text-sm font-medium border border-red-100">
                        {errorMsg}
                      </div>
                    )}

                    <div className="space-y-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="relative group">
                          <input required type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Full Name" className="w-full pb-4 bg-transparent border-b-2 border-gray-200 focus:border-red-600 outline-none transition-all text-lg font-medium placeholder-gray-400" />
                        </div>
                        <div className="relative group">
                          <input required type="tel" name="mobile" value={formData.mobile} onChange={handleChange} placeholder="WhatsApp Number" className="w-full pb-4 bg-transparent border-b-2 border-gray-200 focus:border-red-600 outline-none transition-all text-lg font-medium placeholder-gray-400" />
                        </div>
                      </div>
                      
                      <div className="relative group">
                        <input required type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" className="w-full pb-4 bg-transparent border-b-2 border-gray-200 focus:border-red-600 outline-none transition-all text-lg font-medium placeholder-gray-400" />
                      </div>
                      
                      <div className="space-y-4">
                        <label className="text-sm font-bold text-gray-400 uppercase tracking-wide">Select Pathway</label>
                        <select name="course" value={formData.course} onChange={handleChange} className="w-full pb-4 bg-transparent border-b-2 border-gray-200 focus:border-red-600 outline-none transition-all text-lg font-medium appearance-none cursor-pointer text-gray-800">
                          <option value="Java Full Stack Development">Java Full Stack Development</option>
                          <option value="MERN Stack Mastery">MERN Stack Mastery</option>
                          <option value="Python for Data Science">Python for Data Science</option>
                          <option value="UI/UX & Frontend">UI/UX & Frontend</option>
                          <option value="Cloud & DevOps">Cloud & DevOps</option>
                        </select>
                      </div>
                    </div>

                    <button disabled={loading} type="submit" className="group w-full bg-[#1a1a1a] text-white py-6 rounded-2xl font-bold text-xl hover:bg-red-600 transition-all duration-500 flex items-center justify-center gap-4 shadow-xl hover:shadow-red-200 disabled:opacity-70 disabled:cursor-not-allowed">
                      {loading ? "PROCESSING..." : "START MY JOURNEY"} 
                      {!loading && <Send size={20} className="group-hover:translate-x-2 transition-transform" />}
                    </button>
                  </form>
                )}
              </div>
              
              {/* Trust Badge */}
              <div className="mt-8 flex items-center justify-center gap-6 text-gray-400 font-medium">
                <div className="flex items-center gap-2"><Users size={20} className="text-red-500"/> 5k+ Students</div>
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                <div className="flex items-center gap-2 text-gray-900 font-bold">Project InfoTech</div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* --- Section 3: Value Add / Process (Keeps page length substantial) --- */}
      <div className="bg-white border-t border-gray-100 py-24 px-6 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-black mb-4">What happens next?</h2>
            <p className="text-gray-500 text-lg">We value your time. Here is our straightforward process once you submit your details.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
            {/* Connecting Line for Desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-100 -translate-y-1/2 z-0"></div>

            <div className="relative z-10 bg-white p-8 rounded-3xl border border-gray-100 shadow-sm text-center">
              <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3">1. Quick Review</h3>
              <p className="text-gray-500">Our team evaluates your current profile and selected pathway within 2-4 working hours.</p>
            </div>

            <div className="relative z-10 bg-white p-8 rounded-3xl border border-gray-100 shadow-sm text-center shadow-lg transform md:-translate-y-4">
              <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3">2. Strategy Call</h3>
              <p className="text-gray-500">A senior counselor connects with you to discuss industry trends and map out your personalized roadmap.</p>
            </div>

            <div className="relative z-10 bg-white p-8 rounded-3xl border border-gray-100 shadow-sm text-center">
              <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageCircle size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3">3. Onboarding</h3>
              <p className="text-gray-500">Get access to our premium learning portal, mentorship community, and start building.</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ContactPage;